import PropTypes from "prop-types";
import { formatRupees, toRupees } from "@/utils/currency";

/**
 * Simple CSS-based bar chart for earnings visualization.
 * Shows appointment earnings, facility_charge earned, and facility_charge payable for each month.
 * 
 * @param {Object} props
 * @param {Array} props.data - Chart data array with { month, appointmentEarnings, facilityChargeEarned, facilityChargePayable, netEarnings }
 */
export default function EarningsChart({ data = [] }) {
    if (!data.length) {
        return (
            <div className="text-center py-8 text-slate-500">
                No data available for the chart
            </div>
        );
    }

    // Find max value for scaling
    const maxValue = Math.max(
        ...data.flatMap(d => [
            d.appointmentEarnings || 0,
            d.facilityChargeEarned || 0,
            d.facilityChargePayable || 0
        ])
    );

    const getBarHeight = (value) => {
        if (maxValue === 0) return 0;
        return Math.max(4, (value / maxValue) * 150); // Min 4px, max 150px
    };

    return (
        <div className="earnings-chart">
            {/* Legend */}
            <div className="chart-legend">
                <div className="legend-item">
                    <span className="legend-dot bg-emerald-500"></span>
                    <span>Appointment Earnings</span>
                </div>
                <div className="legend-item">
                    <span className="legend-dot bg-blue-500"></span>
                    <span>Consultation Earnings</span>
                </div>
                <div className="legend-item">
                    <span className="legend-dot bg-orange-500"></span>
                    <span>Charges Due</span>
                </div>
            </div>

            {/* Chart */}
            <div className="chart-container">
                {data.map((item, idx) => (
                    <div key={idx} className="chart-group">
                        <div className="chart-bars">
                            {/* Appointment Earnings */}
                            <div className="chart-bar-wrapper" title={`Appointment: ${formatRupees(item.appointmentEarnings)}`}>
                                <div
                                    className="chart-bar bg-emerald-500"
                                    style={{ height: `${getBarHeight(item.appointmentEarnings)}px` }}
                                >
                                    {item.appointmentEarnings > 0 && (
                                        <span className="chart-bar-value">
                                            ₹{toRupees(item.appointmentEarnings).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Facility Charge Earned */}
                            <div className="chart-bar-wrapper" title={`Earned: ${formatRupees(item.facilityChargeEarned)}`}>
                                <div
                                    className="chart-bar bg-blue-500"
                                    style={{ height: `${getBarHeight(item.facilityChargeEarned)}px` }}
                                >
                                    {item.facilityChargeEarned > 0 && (
                                        <span className="chart-bar-value">
                                            ₹{toRupees(item.facilityChargeEarned).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Charges Due */}
                            <div className="chart-bar-wrapper" title={`Due: ${formatRupees(item.facilityChargePayable)}`}>
                                <div
                                    className="chart-bar bg-orange-500"
                                    style={{ height: `${getBarHeight(item.facilityChargePayable)}px` }}
                                >
                                    {item.facilityChargePayable > 0 && (
                                        <span className="chart-bar-value">
                                            ₹{toRupees(item.facilityChargePayable).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="chart-label">{item.month}</div>
                        <div className="chart-net text-sm font-medium"
                            style={{ color: item.netEarnings >= 0 ? '#10b981' : '#ef4444' }}>
                            Net: {formatRupees(item.netEarnings)}
                        </div>
                    </div>
                ))}
            </div>

            <style>{`
                .earnings-chart {
                    padding: 1rem;
                }
                
                .chart-legend {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 1rem;
                    margin-bottom: 1.5rem;
                    justify-content: center;
                }
                
                .legend-item {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    font-size: 0.875rem;
                    color: #64748b;
                }
                
                .legend-dot {
                    width: 12px;
                    height: 12px;
                    border-radius: 2px;
                }
                
                .chart-container {
                    display: flex;
                    justify-content: space-around;
                    align-items: flex-end;
                    gap: 1rem;
                    padding: 1rem 0;
                    min-height: 220px;
                }
                
                .chart-group {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    flex: 1;
                    max-width: 200px;
                }
                
                .chart-bars {
                    display: flex;
                    gap: 4px;
                    align-items: flex-end;
                    height: 160px;
                }
                
                .chart-bar-wrapper {
                    display: flex;
                    flex-direction: column;
                    justify-content: flex-end;
                    align-items: center;
                    cursor: pointer;
                }
                
                .chart-bar {
                    width: 24px;
                    border-radius: 4px 4px 0 0;
                    transition: height 0.3s ease, opacity 0.2s;
                    position: relative;
                    min-height: 4px;
                }

                .chart-bar:hover {
                    opacity: 0.8;
                }
                
                .chart-bar-value {
                    position: absolute;
                    top: -24px;
                    left: 50%;
                    transform: translateX(-50%);
                    font-size: 0.625rem;
                    font-weight: 600;
                    color: #374151;
                    white-space: nowrap;
                }
                
                .chart-label {
                    margin-top: 0.75rem;
                    font-size: 0.875rem;
                    font-weight: 500;
                    color: #374151;
                }
                
                .chart-net {
                    margin-top: 0.25rem;
                }
                
                @media (max-width: 640px) {
                    .chart-container {
                        gap: 0.5rem;
                    }
                    
                    .chart-bar {
                        width: 18px;
                    }
                    
                    .chart-bar-value {
                        font-size: 0.5rem;
                    }
                    
                    .chart-label {
                        font-size: 0.75rem;
                    }
                    
                    .legend-item {
                        font-size: 0.75rem;
                    }
                }
            `}</style>
        </div>
    );
}

EarningsChart.propTypes = {
    data: PropTypes.arrayOf(PropTypes.shape({
        month: PropTypes.string.isRequired,
        appointmentEarnings: PropTypes.number,
        facilityChargeEarned: PropTypes.number,
        facilityChargePayable: PropTypes.number,
        netEarnings: PropTypes.number
    }))
};
